/* ARMATEQ draft: inline collection buying, truthful stock and first-screen mobile CTA.
   The existing ARMATEQCart remains the only cart writer. No app, price or inventory changes. */
(() => {
  'use strict';
  if (window.ARMATEQBuyingEnhancements) return;
  const configNode = document.getElementById('aq-buying-config');
  if (!configNode) return;
  let config;
  try { config = JSON.parse(configNode.textContent); } catch { return; }
  const labels = config.labels;
  const cardStates = new WeakMap();
  const productStates = new WeakMap();
  const productMeta = new Map((config.productVariants || []).map(v => [String(v.id), v]));
  const mobile = matchMedia('(max-width: 989px)');
  let quickCard = null;
  let overlayFrame = 0;
  const node = (tag, className, text) => {
    const el = document.createElement(tag);
    if (className) el.className = className;
    if (text !== undefined) el.textContent = text;
    return el;
  };
  const fill = (text, values) => Object.entries(values).reduce((result, [key, value]) => result.split(`__${key}__`).join(String(value)), text || '');
  const lowStock = (v, threshold = 10) => Boolean(v && v.available === true && v.tracked === true && Number.isFinite(Number(v.inventory)) && Number(v.inventory) > 0 && Number(v.inventory) <= threshold);
  const presale = v => Boolean(v && v.available === true && v.tracked === true && v.inventory_policy === 'continue' && Number.isFinite(Number(v.inventory)) && Number(v.inventory) <= 0 && v.presale_date);
  const presaleText = (v, size) => presale(v) ? fill(labels.presaleNotice, { SIZE: size, DATE: v.presale_date }) : '';
  const parseCard = card => {
    try {
      const data = JSON.parse(card.querySelector('[data-nm-quick-add-data]').textContent);
      return Array.isArray(data.options) && Array.isArray(data.variants) ? data : null;
    } catch { return null; }
  };
  const ensureNotice = (root, before) => {
    let notice = root.querySelector('[data-aq-presale-notice]');
    if (!notice && before?.parentNode) {
      notice = node('p', 'aq-presale-notice');
      notice.dataset.aqPresaleNotice = '';
      notice.setAttribute('role', 'status');
      notice.hidden = true;
      before.before(notice);
    }
    return notice;
  };
  const setNotice = (notice, text) => {
    if (!notice) return;
    if (notice.textContent !== text) notice.textContent = text;
    notice.hidden = !text;
  };

  function updateProduct(root) {
    const sameProduct = String(root.dataset.productId) === String(config.productId);
    const chosen = root.variant;
    const v = chosen && sameProduct ? { ...chosen, ...productMeta.get(String(chosen.id)) } : chosen;
    const stock = root.querySelector('[data-nm-stock]');
    const threshold = Number(stock?.dataset.nmStockThreshold) || 10;
    const isLow = Boolean(stock) && lowStock(v, threshold);
    const text = isLow ? (stock.dataset.nmStockText || fill(labels.onlyLeft, { COUNT: '[count]' })).replace('[count]', String(v.inventory)) : '';
    if (stock) {
      stock.hidden = !isLow;
      const label = stock.querySelector('[data-nm-stock-label]');
      if (label) label.textContent = text;
    }
    const size = chosen?.options?.[Number(root.dataset.sizeIndex)] || '';
    const shippingText = presaleText(v, size);
    setNotice(ensureNotice(root, root.querySelector('[data-nm-product-add]')), shippingText);
    const sticky = root.querySelector('[data-nm-sticky]');
    if (!sticky) return;
    let stickyNote = sticky.querySelector('[data-aq-sticky-note]');
    if (!stickyNote) {
      stickyNote = node('p', 'aq-sticky-note');
      stickyNote.dataset.aqStickyNote = '';
      stickyNote.hidden = true;
      sticky.prepend(stickyNote);
    }
    stickyNote.classList.toggle('aq-sticky-note--low', isLow);
    const noteText = shippingText || text;
    if (stickyNote.dataset.text !== noteText) {
      stickyNote.dataset.text = noteText;
      stickyNote.replaceChildren();
      if (isLow) {
        const dot = node('span', 'aq-stock-dot');
        dot.setAttribute('aria-hidden', 'true');
        stickyNote.append(dot);
      }
      stickyNote.append(node('span', '', noteText));
    }
    stickyNote.hidden = !noteText;
    productStates.get(root)?.sync();
  }

  function bindProduct(root) {
    if (!root.initialized || !root.isConnected) return;
    const old = productStates.get(root);
    if (old && old.signal === root.signal) return;
    old?.destroy();
    const sticky = root.querySelector('[data-nm-sticky]');
    const add = root.querySelector('[data-nm-product-add]');
    if (!sticky || !add) return;
    // The native observer only reveals the bar AFTER passing the main CTA.
    // Replace that visibility observer, not the existing purchasing handlers.
    root.observer?.disconnect();
    root.classList.add('aq-mobile-buying');
    document.body.classList.add('aq-mobile-buying-page');
    const controller = new AbortController();
    const sync = () => {
      if (!root.isConnected) return;
      const box = add.getBoundingClientRect();
      const mainVisible = box.height > 0 && box.top >= 0 && box.bottom <= window.innerHeight;
      const focusedInput = document.activeElement?.matches('input:not([type="radio"]):not([type="checkbox"]), textarea');
      const keyboardOpen = focusedInput && window.visualViewport && visualViewport.height < window.innerHeight * .72;
      sticky.hidden = !mobile.matches || mainVisible || Boolean(keyboardOpen) || document.body.classList.contains('aq-buying-overlay');
      if (!sticky.hidden) document.body.style.setProperty('--aq-sticky-height', `${Math.ceil(sticky.getBoundingClientRect().height) + 12}px`);
    };
    const observer = new IntersectionObserver(sync, { threshold: [0, 1] });
    observer.observe(add);
    const resize = new ResizeObserver(sync);
    resize.observe(sticky);
    resize.observe(add);
    window.addEventListener('resize', sync, { signal: controller.signal });
    window.addEventListener('pageshow', sync, { signal: controller.signal });
    window.visualViewport?.addEventListener('resize', sync, { signal: controller.signal });
    const destroy = () => { observer.disconnect(); resize.disconnect(); controller.abort(); productStates.delete(root); };
    root.signal?.addEventListener('abort', destroy, { once: true });
    productStates.set(root, { signal: root.signal, sync, destroy });
    updateProduct(root);
    sync();
  }

  function inspectSize(state, button) {
    const v = state.byId.get(String(button.dataset.variantId));
    if (!v || button.hidden) return;
    const stock = state.inline.querySelector('[data-aq-inline-stock]');
    const visible = lowStock(v, state.threshold);
    stock.hidden = !visible;
    state.inline.querySelector('[data-aq-inline-stock-label]').textContent = visible ? `${button.dataset.size}: ${fill(labels.onlyLeft, { COUNT: v.inventory })}` : '';
  }
  function renderCard(state, reset = false) {
    const { card, inline, data } = state;
    if (reset) {
      state.selected = null;
      inline.querySelector('[data-aq-inline-stock]').hidden = true;
      setNotice(inline.querySelector('[data-aq-inline-message]'), '');
      setNotice(inline.querySelector('[data-aq-inline-error]'), '');
      inline.querySelector('[data-aq-inline-recovery]').hidden = true;
    }
    const colour = state.colourIndex < 0 ? '' : (card.dataset.nmColour || data.selected_colour);
    inline.querySelectorAll('[data-aq-inline-size]').forEach(button => {
      const v = state.byId.get(String(button.dataset.variantId));
      const sameColour = state.colourIndex < 0 || v?.options[state.colourIndex] === colour;
      const eligible = Boolean(v && (!data.sale_only || Number(v.compare_at_price) > Number(v.price)));
      button.hidden = !sameColour || !eligible;
      button.disabled = state.busy || !v?.available || !eligible;
      button.setAttribute('aria-pressed', String(state.selected === String(v?.id)));
    });
    inline.querySelectorAll('[data-aq-presale-colour]').forEach(p => { p.hidden = p.dataset.aqPresaleColour !== colour; });
  }
  function initialiseCard(card) {
    if (cardStates.has(card)) return;
    const inline = card.querySelector('[data-aq-inline-buying]');
    if (!inline) return;
    const data = parseCard(card);
    if (!data) return;
    const state = { card, inline, data, byId: new Map(data.variants.map(v => [String(v.id), v])), colourIndex: data.options.findIndex(o => /^(color|colour)$/i.test(o.name)), threshold: Number(inline.dataset.aqStockThreshold) || 10, selected: null, busy: false, lastAdd: -Infinity };
    cardStates.set(card, state);
    renderCard(state);
    card.classList.add('aq-inline-ready');
  }
  async function addSize(button) {
    const card = button.closest('[data-nm-card]');
    const state = cardStates.get(card);
    if (!state || state.busy || button.disabled || button.hidden || card.getAttribute('aria-busy') === 'true' || performance.now() - state.lastAdd < 600) return;
    const v = state.byId.get(String(button.dataset.variantId));
    if (!v?.available || !Number.isSafeInteger(Number(v.id)) || Number(v.id) <= 0) return;
    if (state.colourIndex >= 0 && v.options[state.colourIndex] !== card.dataset.nmColour) return;
    if (state.data.sale_only && Number(v.compare_at_price) <= Number(v.price)) return;
    const message = state.inline.querySelector('[data-aq-inline-message]');
    const errorNode = state.inline.querySelector('[data-aq-inline-error]');
    const recovery = state.inline.querySelector('[data-aq-inline-recovery]');
    setNotice(errorNode, '');
    recovery.hidden = true;
    if (!window.ARMATEQCart?.addItems) {
      setNotice(errorNode, labels.error);
      recovery.hidden = false;
      return;
    }
    state.busy = true;
    state.lastAdd = performance.now();
    state.selected = String(v.id);
    card.setAttribute('aria-busy', 'true');
    inspectSize(state, button);
    renderCard(state);
    setNotice(message, labels.adding);
    // Match the exact tapped size's price, not the cheapest size of the colour.
    const price = card.querySelector('[data-nm-card-price]');
    if (price) {
      price.replaceChildren(node('span', '', v.price_formatted));
      if (Number(v.compare_at_price) > Number(v.price)) price.append(node('s', '', v.compare_formatted));
    }
    try {
      // This API refreshes AND opens the existing native cart drawer after success.
      // Do not separately fetch/add/retry: uncertain network outcomes must not duplicate items.
      await window.ARMATEQCart.addItems([{ id: Number(v.id), quantity: 1 }]);
      setNotice(message, `${labels.added} · ${[card.dataset.nmColour, button.dataset.size].filter(Boolean).join(' / ')}`);
    } catch (error) {
      setNotice(message, '');
      setNotice(errorNode, error.cartWasModified ? labels.addedRefreshError : (error.message || labels.error));
      recovery.hidden = false;
    } finally {
      state.busy = false;
      state.lastAdd = performance.now();
      card.removeAttribute('aria-busy');
      renderCard(state);
    }
  }

  function updateQuickNotice() {
    const dialog = document.querySelector('[data-nm-quick-add-dialog]');
    if (!dialog) return;
    const notice = ensureNotice(dialog, dialog.querySelector('[data-nm-quick-add-submit]'));
    setNotice(notice, ''); // Always clear first, including size-only products.
    if (!dialog.open || !quickCard) return;
    const data = parseCard(quickCard);
    if (!data) return;
    const sizeIndex = data.options.findIndex(o => /size/i.test(o.name));
    if (sizeIndex < 0) return;
    const selected = [];
    dialog.querySelectorAll('[data-nm-quick-add-option][aria-pressed="true"]').forEach(b => { selected[Number(b.dataset.nmQuickAddOption)] = b.dataset.value; });
    const v = data.variants.find(v => v.options.every((value, index) => selected[index] === value));
    setNotice(notice, presaleText(v, v?.options[sizeIndex]));
  }
  function syncOverlays() {
    overlayFrame = 0;
    const open = Boolean(document.querySelector('dialog[open]')) || [...document.querySelectorAll('[role="dialog"][aria-modal="true"]')].some(el => !el.hidden && el.getAttribute('aria-hidden') !== 'true' && el.getClientRects().length > 0);
    document.body.classList.toggle('aq-buying-overlay', open);
    document.querySelectorAll('nm-product').forEach(root => productStates.get(root)?.sync());
  }
  function init(scope = document) {
    if (config.collection) {
      if (scope.matches?.('[data-nm-card]')) initialiseCard(scope);
      scope.querySelectorAll?.('[data-nm-card]').forEach(initialiseCard);
    }
    if (scope.matches?.('nm-product')) bindProduct(scope);
    scope.querySelectorAll?.('nm-product').forEach(bindProduct);
    syncOverlays();
  }
  document.addEventListener('nm:card-colour-change', event => {
    const state = cardStates.get(event.target.closest('[data-nm-card]'));
    if (state) renderCard(state, true);
  });
  document.addEventListener('nm:variant:change', event => {
    const root = event.target.closest('nm-product');
    if (!root) return;
    updateProduct(root);
    queueMicrotask(() => bindProduct(root));
  });
  document.addEventListener('click', event => {
    const target = event.target instanceof Element ? event.target : null;
    const size = target?.closest('[data-aq-inline-size]');
    if (size && config.collection) { event.preventDefault(); addSize(size); return; }
    const trigger = target?.closest('[data-nm-quick-add]');
    if (trigger) quickCard = trigger.closest('[data-nm-card]');
    if (trigger || target?.closest('[data-nm-quick-add-option]')) queueMicrotask(updateQuickNotice);
  }, true);
  ['pointerover', 'focusin'].forEach(type => document.addEventListener(type, event => {
    const button = event.target instanceof Element ? event.target.closest('[data-aq-inline-size]') : null;
    if (!button || !config.collection || button.disabled) return;
    const state = cardStates.get(button.closest('[data-nm-card]'));
    if (state && !state.busy) inspectSize(state, button);
  }));
  document.addEventListener('shopify:section:load', event => queueMicrotask(() => init(event.target)));
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('nm-product').forEach(root => productStates.get(root)?.destroy()));
  const start = () => {
    init();
    // Watch only additions and modal state, never text/class changes from cart widgets.
    new MutationObserver(records => {
      for (const record of records) {
        if (record.type === 'childList') {
          record.addedNodes.forEach(el => { if (el.nodeType === 1 && (el.matches?.('[data-nm-card], nm-product') || el.querySelector?.('[data-nm-card], nm-product'))) init(el); });
        } else {
          if (record.target.matches('[data-nm-quick-add-dialog]') && record.attributeName === 'open') queueMicrotask(updateQuickNotice);
          if (!overlayFrame) overlayFrame = requestAnimationFrame(syncOverlays);
        }
      }
    }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['open', 'aria-modal', 'aria-hidden'] });
  };
  window.ARMATEQBuyingEnhancements = { version: 'inline-stock-cta-1' };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
